// Logic behind the customer page, kept out of the components so it can be unit
// tested: expiry wording, billing totals, the edit form's validation and its
// "has anything changed" check.

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// "Expires in 12 days" / "Expired 3 days ago" with a tone for colouring:
// expired (red), soon (amber, 30 days or less), ok (neutral). null for no date.
export const expiryInfo = (value, now = new Date()) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const days = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
  if (days < 0) return { days, tone: 'expired', label: days === -1 ? 'Expired yesterday' : `Expired ${-days} days ago` };
  if (days === 0) return { days, tone: 'soon', label: 'Expires today' };
  if (days === 1) return { days, tone: 'soon', label: 'Expires tomorrow' };
  return { days, tone: days <= 30 ? 'soon' : 'ok', label: `Expires in ${days} days` };
};

// The domain that needs attention first (an already-expired one beats the
// next to expire). null when no domain has an expiry date.
export const nextExpiry = (domains, now = new Date()) => {
  const dated = (domains || [])
    .map((d) => ({ domain: d, info: expiryInfo(d.expiry_date, now) }))
    .filter((x) => x.info);
  if (dated.length === 0) return null;
  dated.sort((a, b) => a.info.days - b.info.days);
  return dated[0];
};

export const isOverdueInvoice = (invoice, now = new Date()) => !!invoice
  && invoice.payment_status === 'Pending Payment'
  && !!invoice.payment_due_date
  && new Date(invoice.payment_due_date) < now;

export const summariseBilling = (invoices, now = new Date()) => (invoices || []).reduce((acc, inv) => {
  if (inv.payment_status === 'Paid') {
    acc.paidCount += 1;
    acc.paidTotal += Number(inv.amount_paid || inv.amount_payable || 0);
  } else if (isOverdueInvoice(inv, now)) {
    acc.overdueCount += 1;
    acc.overdueTotal += Number(inv.amount_payable || 0);
  } else if (inv.payment_status === 'Pending Payment') {
    acc.pendingCount += 1;
  }
  return acc;
}, { paidCount: 0, pendingCount: 0, overdueCount: 0, paidTotal: 0, overdueTotal: 0 });

export const EDIT_FIELDS = [
  'first_name', 'last_name', 'companyName', 'status', 'email', 'phoneNumber',
  'address1', 'address2', 'city', 'county', 'postcode', 'country', 'notes'
];

export const toEditForm = (customer) => Object.fromEntries(
  EDIT_FIELDS.map((field) => [field, (customer && customer[field]) || ''])
);

// Whitespace at the ends doesn't count as a change.
export const isDirty = (initial, current) => EDIT_FIELDS.some(
  (field) => String(initial[field] || '').trim() !== String(current[field] || '').trim()
);

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Returns { field: message } - empty when the form is fine. Messages say how
// to fix it, not just what is wrong.
export const validateCustomerForm = (form) => {
  const errors = {};
  if (!String(form.first_name || '').trim()) errors.first_name = 'Enter a first name.';
  if (!String(form.last_name || '').trim() && !String(form.companyName || '').trim()) {
    errors.last_name = 'Enter a last name, or a company name.';
  }
  const email = String(form.email || '').trim();
  if (!email) errors.email = 'Enter an email address, e.g. name@example.com.';
  else if (!EMAIL.test(email)) errors.email = 'That doesn\'t look like an email address. Check for a missing @ or domain.';
  const phone = String(form.phoneNumber || '').trim();
  if (phone && !/^[+()\-.\s\d]{6,25}$/.test(phone)) errors.phoneNumber = 'Use digits, spaces and + ( ) - only, e.g. +44 20 7946 0000.';
  return errors;
};

// Only what the API accepts, trimmed.
export const toPayload = (form) => Object.fromEntries(
  EDIT_FIELDS.map((field) => [field, typeof form[field] === 'string' ? form[field].trim() : form[field]])
);

export const STATUS_OPTIONS = ['Active', 'Inactive', 'Suspended', 'Cancelled'];

export const customerDisplayName = (customer) => {
  if (!customer) return '';
  return customer.companyName || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email || 'Customer';
};
